import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BookOpenText,
  Bell,
  History,
  Power,
  Rows3,
  ShieldCheck,
  Sparkles,
  Tag,
  ToggleLeft,
} from "lucide-react";
import { Card, CardBody, CardHeader, StatusPill, SkeletonText, ErrorState, EmptyState } from "@/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useCurrentConfig,
  useCurrentDailyReflection,
  useCurrentDailyVerse,
  useNotifications,
  usePublicHealth,
} from "@/lib/queries";
import { FEATURE_FLAG_KEYS, FEATURE_FLAG_LABELS, type FeatureFlags } from "@/lib/api";
import { APP_MODE_META, DAILY_CONTENT_STATUS_META, NOTIFICATION_STATUS_META } from "@/lib/utils/statusMeta";
import { formatDateTime, formatRelative } from "@/lib/utils/format";

export function DashboardPage() {
  const configQuery = useCurrentConfig();
  const verseQuery = useCurrentDailyVerse();
  const reflectionQuery = useCurrentDailyReflection();
  const healthQuery = usePublicHealth();
  const notificationsQuery = useNotifications();

  const config = configQuery.data;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Vision general del estado de IESAPP y accesos rapidos a las secciones de control."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Estado general de IESAPP"
            description="Modo activo, disponibilidad y ultima publicacion de configuracion."
            action={
              <Link
                to="/app-status"
                className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline"
              >
                Gestionar <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            }
          />
          <CardBody>
            {configQuery.isLoading && <SkeletonText lines={4} />}
            {configQuery.isError && (
              <ErrorState description="No se pudo cargar la configuracion." onRetry={() => configQuery.refetch()} />
            )}
            {config && (
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">Modo</dt>
                  <dd className="mt-1">
                    <StatusPill tone={APP_MODE_META[config.appMode].tone}>
                      {APP_MODE_META[config.appMode].label}
                    </StatusPill>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">Disponible</dt>
                  <dd className="mt-1 text-sm font-medium text-ink">
                    {config.appEnabled ? "Si" : "No"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">Version minima</dt>
                  <dd className="mt-1 text-sm font-medium text-ink">
                    {config.minimumVersion}{" "}
                    <span className="text-ink-faint">(code {config.minimumVersionCode})</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">
                    Actualizacion forzosa
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-ink">{config.forceUpdate ? "Si" : "No"}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">
                    Ultima modificacion
                  </dt>
                  <dd className="mt-1 text-sm text-ink">
                    {formatRelative(config.createdAt)} - {formatDateTime(config.createdAt)}
                    {config.createdByEmail && (
                      <span className="text-ink-faint"> por {config.createdByEmail}</span>
                    )}
                    <span className="text-ink-faint"> - revision #{config.revision}</span>
                  </dd>
                </div>
                {config.maintenanceMessage && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-ink-faint">
                      Mensaje de mantenimiento
                    </dt>
                    <dd className="mt-1 text-sm text-ink">{config.maintenanceMessage}</dd>
                  </div>
                )}
              </dl>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Estado del backend" description="Endpoint publico de salud" />
          <CardBody>
            {healthQuery.isLoading && <SkeletonText lines={2} />}
            {healthQuery.isError && (
              <ErrorState
                title="Backend no disponible"
                description="No se pudo contactar con el endpoint publico de salud."
                onRetry={() => healthQuery.refetch()}
              />
            )}
            {healthQuery.data && (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-success-100">
                  <Activity className="h-5 w-5 text-success-700" aria-hidden="true" />
                </div>
                <div>
                  <StatusPill tone={healthQuery.data.status === "ok" ? "success" : "danger"}>
                    {healthQuery.data.status}
                  </StatusPill>
                  <p className="mt-1 text-xs text-ink-faint">
                    {formatRelative(healthQuery.data.updatedAt)}
                  </p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Funciones"
            description="Resumen de feature flags"
            action={
              <Link to="/features" className="text-sm font-medium text-brand-700 hover:underline">
                Ver todo
              </Link>
            }
          />
          <CardBody>
            {configQuery.isLoading && <SkeletonText lines={3} />}
            {config && (
              <FeatureFlagsSummary features={config.features} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Olivia"
            description="Asistente conversacional"
            action={
              <Link to="/features" className="text-sm font-medium text-brand-700 hover:underline">
                Gestionar
              </Link>
            }
          />
          <CardBody>
            {configQuery.isLoading && <SkeletonText lines={2} />}
            {config && (
              <div className="space-y-2">
                <StatusPill tone={config.olivia.enabled ? "success" : "neutral"}>
                  {config.olivia.enabled ? "Habilitada" : "Deshabilitada"}
                </StatusPill>
                <p className="text-sm text-ink-muted">
                  Modo de acceso: <span className="font-medium text-ink">{config.olivia.accessMode}</span>
                </p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Versiculo diario"
            action={
              <Link to="/daily-content" className="text-sm font-medium text-brand-700 hover:underline">
                Ver
              </Link>
            }
          />
          <CardBody>
            {verseQuery.isLoading && <SkeletonText lines={3} />}
            {verseQuery.isError && (
              <ErrorState description="No se pudo cargar el versiculo diario." onRetry={() => verseQuery.refetch()} />
            )}
            {verseQuery.isSuccess && !verseQuery.data && (
              <p className="text-sm text-ink-faint">Sin versiculo activo para hoy.</p>
            )}
            {verseQuery.data && (
              <div className="space-y-1.5">
                <StatusPill tone={DAILY_CONTENT_STATUS_META[verseQuery.data.status].tone}>
                  {DAILY_CONTENT_STATUS_META[verseQuery.data.status].label}
                </StatusPill>
                <p className="text-sm font-medium text-ink">{verseQuery.data.reference ?? "Sin referencia"}</p>
                <p className="text-xs text-ink-faint">{formatDateTime(verseQuery.data.date)}</p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Reflexion diaria"
            action={
              <Link to="/daily-content" className="text-sm font-medium text-brand-700 hover:underline">
                Ver
              </Link>
            }
          />
          <CardBody>
            {reflectionQuery.isLoading && <SkeletonText lines={3} />}
            {reflectionQuery.isError && (
              <ErrorState
                description="No se pudo cargar la reflexion diaria."
                onRetry={() => reflectionQuery.refetch()}
              />
            )}
            {reflectionQuery.isSuccess && !reflectionQuery.data && (
              <p className="text-sm text-ink-faint">Sin reflexion activa para hoy.</p>
            )}
            {reflectionQuery.data && (
              <div className="space-y-1.5">
                <StatusPill tone={DAILY_CONTENT_STATUS_META[reflectionQuery.data.status].tone}>
                  {DAILY_CONTENT_STATUS_META[reflectionQuery.data.status].label}
                </StatusPill>
                <p className="text-sm font-medium text-ink">{reflectionQuery.data.title ?? "Sin titulo"}</p>
                <p className="text-xs text-ink-faint">{formatDateTime(reflectionQuery.data.date)}</p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Ultimas notificaciones"
            description="Campanas mas recientes"
            action={
              <Link to="/notifications" className="text-sm font-medium text-brand-700 hover:underline">
                Ver todo
              </Link>
            }
          />
          <CardBody>
            {notificationsQuery.isLoading && <SkeletonText lines={3} />}
            {notificationsQuery.isError && (
              <ErrorState
                description="No se pudieron cargar las notificaciones."
                onRetry={() => notificationsQuery.refetch()}
              />
            )}
            {notificationsQuery.isSuccess && notificationsQuery.data.length === 0 && (
              <EmptyState title="Sin notificaciones todavia" />
            )}
            {notificationsQuery.isSuccess && notificationsQuery.data.length > 0 && (
              <ul className="space-y-2.5">
                {notificationsQuery.data.slice(0, 3).map((n) => (
                  <li key={n.id} className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{n.title}</p>
                      <p className="text-xs text-ink-faint">{formatRelative(n.updatedAt)}</p>
                    </div>
                    <StatusPill tone={NOTIFICATION_STATUS_META[n.status].tone}>
                      {NOTIFICATION_STATUS_META[n.status].label}
                    </StatusPill>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader title="Accesos rapidos" />
          <CardBody>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <QuickLink to="/app-status" icon={Power} label="Estado de la app" />
              <QuickLink to="/features" icon={ToggleLeft} label="Funciones" />
              <QuickLink to="/home-blocks" icon={Rows3} label="Inicio" />
              <QuickLink to="/daily-content" icon={BookOpenText} label="Contenido diario" />
              <QuickLink to="/notifications" icon={Bell} label="Notificaciones" />
              <QuickLink to="/versions" icon={Tag} label="Versiones" />
              <QuickLink to="/audit" icon={History} label="Auditoria" />
              <QuickLink to="/security" icon={ShieldCheck} label="Seguridad" />
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function FeatureFlagsSummary({ features }: { features: FeatureFlags }) {
  const enabled = FEATURE_FLAG_KEYS.filter((key) => features[key]);
  const disabled = FEATURE_FLAG_KEYS.filter((key) => !features[key]);

  return (
    <div className="space-y-2">
      <p className="text-sm text-ink">
        <span className="font-semibold text-success-700">{enabled.length}</span> activas de{" "}
        {FEATURE_FLAG_KEYS.length}
      </p>
      {disabled.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {disabled.map((key) => (
            <StatusPill key={key} tone="neutral">
              {FEATURE_FLAG_LABELS[key]}
            </StatusPill>
          ))}
        </div>
      ) : (
        <p className="text-xs text-ink-faint">Todas las funciones estan activas.</p>
      )}
    </div>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Sparkles;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-start gap-2 rounded-lg border border-ink/10 p-3 text-sm font-medium text-ink transition-colors hover:border-brand-300 hover:bg-brand-50"
    >
      <Icon className="h-5 w-5 text-brand-700" aria-hidden="true" />
      {label}
    </Link>
  );
}
